import React, { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, HelpCircle, X } from 'lucide-react';
import type { ConfirmRequest } from './types';

type ConfirmModalProps = {
  request: ConfirmRequest | null;
  onClose: () => void;
};

export default function ConfirmModal({ request, onClose }: ConfirmModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!request) {
      setVisible(false);
      setBusy(false);
      setActionError(null);
      return;
    }

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    setVisible(true);
    setBusy(false);
    setActionError(null);

    const frame = window.requestAnimationFrame(() => {
      confirmBtnRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [request]);

  useEffect(() => {
    if (!request || !visible) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) {
        event.preventDefault();
        finish(false);
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [request, visible, busy]);

  const finish = (value: boolean) => {
    if (!request) return;
    setVisible(false);
    window.setTimeout(() => {
      request.resolve(value);
      onClose();
      previouslyFocused.current?.focus?.();
    }, 180);
  };

  const handleCancel = () => {
    if (busy) return;
    finish(false);
  };

  const handleConfirm = async () => {
    if (!request || busy) return;
    if (!request.onConfirm) {
      finish(true);
      return;
    }

    setBusy(true);
    setActionError(null);
    try {
      await request.onConfirm();
      finish(true);
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Não foi possível concluir a operação.';
      setActionError(message);
      setBusy(false);
    }
  };

  if (!request) return null;

  const isDanger = request.type === 'danger';
  const confirmText = request.confirmText || (isDanger ? 'Remover' : 'Confirmar');
  const cancelText = request.cancelText || 'Cancelar';
  const Icon = isDanger ? AlertTriangle : HelpCircle;

  return (
    <div
      className={[
        'fixed inset-0 z-[1200] flex items-center justify-center p-4 sm:p-6',
        'transition-opacity duration-200',
        visible ? 'opacity-100' : 'opacity-0',
      ].join(' ')}
    >
      <button
        type="button"
        aria-label="Fechar diálogo"
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={handleCancel}
        disabled={busy}
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={request.message || actionError ? descriptionId : undefined}
        className={[
          'relative w-full max-w-md rounded-2xl border border-[#E8E0EC] bg-white shadow-[0_20px_60px_rgba(51,0,43,0.18)]',
          'transition-all duration-200',
          visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
        ].join(' ')}
      >
        <button
          type="button"
          onClick={handleCancel}
          disabled={busy}
          className="absolute right-3 top-3 rounded-lg p-1.5 text-[#9A8FA0] transition-colors hover:bg-[#F6F1F5] hover:text-[#660055] outline-none disabled:opacity-50"
          aria-label="Fechar"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-6 pb-5 pt-6">
          <div className="flex items-start gap-3.5 pr-6">
            <div
              className={[
                'mt-0.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl',
                isDanger ? 'bg-[#FDECEC] text-[#C62828]' : 'bg-[#F3E6F0] text-[#660055]',
              ].join(' ')}
            >
              <Icon className="h-5 w-5" strokeWidth={2.2} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="text-lg font-semibold leading-snug text-[#1F1F1F]">
                {request.title}
              </h2>
              {(request.message || actionError) && (
                <div id={descriptionId} className="mt-2 space-y-2">
                  {request.message && (
                    <p className="text-sm leading-relaxed text-[#6B6B6B] whitespace-pre-wrap">
                      {request.message}
                    </p>
                  )}
                  {actionError && (
                    <p className="text-sm leading-relaxed text-[#C62828]">{actionError}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleCancel}
              disabled={busy}
              className="inline-flex h-11 items-center justify-center rounded-xl border border-[#E0D6E4] bg-white px-4 text-sm font-semibold text-[#4A3F4E] transition-colors hover:bg-[#FAF7FB] outline-none disabled:opacity-60"
            >
              {cancelText}
            </button>
            <button
              ref={confirmBtnRef}
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              className={[
                'inline-flex h-11 min-w-[120px] items-center justify-center rounded-xl px-4 text-sm font-semibold text-white transition-colors outline-none disabled:opacity-70',
                isDanger
                  ? 'bg-[#C62828] hover:bg-[#B71C1C]'
                  : 'bg-[#660055] hover:bg-[#520044]',
              ].join(' ')}
            >
              {busy ? 'Processando...' : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
